import React, { useState, useEffect } from 'react';
import { getLeagueMatches } from '../../services/leagueService';
import GeneratePlayoffsModal from './GeneratePlayoffsModal';
import { IconTrophy, IconMedal, IconArrowNarrowRight } from '@tabler/icons-react';

const StandingsTab = ({ leagueId, league, members, isAdmin }) => {
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlayoffsModal, setShowPlayoffsModal] = useState(false);

  useEffect(() => {
    calculateStandings();
  }, [leagueId, members]);

  const calculateStandings = async () => {
    try {
      setLoading(true);
      const matchesData = await getLeagueMatches(leagueId);
      setMatches(matchesData);

      // Calculate standings from completed matches
      const standingsMap = new Map();

      // Initialize all members
      members.forEach(member => {
        standingsMap.set(member.userId, {
          participantId: member.userId,
          participantName: `${member.firstName} ${member.lastName}`,
          wins: 0,
          losses: 0,
          matchesPlayed: 0,
          winPercentage: 0,
          pointsFor: 0,
          pointsAgainst: 0
        });
      });

      // Process completed matches
      matchesData
        .filter(match => match.status === 'completed')
        .forEach(match => {
          const p1Stats = standingsMap.get(match.participant1Id);
          const p2Stats = standingsMap.get(match.participant2Id);

          if (p1Stats && p2Stats) {
            // Update matches played
            p1Stats.matchesPlayed++;
            p2Stats.matchesPlayed++;

            // Update scores
            p1Stats.pointsFor += match.participant1Score || 0;
            p1Stats.pointsAgainst += match.participant2Score || 0;
            p2Stats.pointsFor += match.participant2Score || 0;
            p2Stats.pointsAgainst += match.participant1Score || 0;

            // Update wins/losses
            if (match.winnerId === match.participant1Id) {
              p1Stats.wins++;
              p2Stats.losses++;
            } else if (match.winnerId === match.participant2Id) {
              p2Stats.wins++;
              p1Stats.losses++;
            }

            // Calculate win percentage
            p1Stats.winPercentage = p1Stats.matchesPlayed > 0
              ? (p1Stats.wins / p1Stats.matchesPlayed) * 100
              : 0;
            p2Stats.winPercentage = p2Stats.matchesPlayed > 0
              ? (p2Stats.wins / p2Stats.matchesPlayed) * 100
              : 0;
          }
        });

      // Convert to array and sort
      const standingsArray = Array.from(standingsMap.values())
        .sort((a, b) => {
          // Sort by wins first
          if (b.wins !== a.wins) return b.wins - a.wins;
          // Then by win percentage
          if (b.winPercentage !== a.winPercentage) return b.winPercentage - a.winPercentage;
          // Then by point differential
          const aDiff = a.pointsFor - a.pointsAgainst;
          const bDiff = b.pointsFor - b.pointsAgainst;
          return bDiff - aDiff;
        });

      // Add rank
      standingsArray.forEach((standing, index) => {
        standing.rank = index + 1;
      });

      setStandings(standingsArray);
    } catch (error) {
      console.error('Error calculating standings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: <IconTrophy size={20} />, class: 'first' };
    if (rank === 2) return { icon: <IconMedal size={20} />, class: 'second' };
    if (rank === 3) return { icon: <IconMedal size={20} />, class: 'third' };
    return { icon: null, class: '' };
  };

  const handlePlayoffsGenerated = () => {
    setShowPlayoffsModal(false);
    // Reload standings/matches if needed
    calculateStandings();
  };

  // Check if league format supports playoffs
  const canGeneratePlayoffs = league?.format === 'standard-league' || league?.format === 'round-robin';
  const hasPlayoffMatches = matches.some(m => m.isPlayoff);

  return (
    <section className="ls-standings-tab">
      <header className="ls-standings-header">
        <h2>Standings</h2>
        {isAdmin && canGeneratePlayoffs && !hasPlayoffMatches && standings.length >= 2 && (
          <button
            className="ls-btn-generate-playoffs"
            onClick={() => setShowPlayoffsModal(true)}
          >
            <IconArrowNarrowRight size={20} />
            <span>Generate Playoffs</span>
          </button>
        )}
      </header>

      {loading ? (
        <div className="ls-loading-standings">
          <p>Loading standings...</p>
        </div>
      ) : standings.length === 0 || standings.every(s => s.matchesPlayed === 0) ? (
        <div className="ls-empty-standings">
          <p>Standings will appear once matches are completed.</p>
        </div>
      ) : (
        <div className="ls-standings-table-wrapper">
          <table className="ls-standings-table">
            <thead>
              <tr>
                <th className="ls-rank-col">Rank</th>
                <th className="ls-participant-col">Participant</th>
                <th className="ls-stat-col">Played</th>
                <th className="ls-stat-col">Wins</th>
                <th className="ls-stat-col">Losses</th>
                <th className="ls-stat-col">Win %</th>
                <th className="ls-stat-col">PF</th>
                <th className="ls-stat-col">PA</th>
                <th className="ls-stat-col">Diff</th>
              </tr>
            </thead>
            <tbody>
              {standings.map(standing => {
                const rankBadge = getRankBadge(standing.rank);
                const pointDiff = standing.pointsFor - standing.pointsAgainst;
                return (
                  <tr key={standing.participantId} className={`ls-standing-row${rankBadge.class}`}>
                    <td className="ls-rank-col">
                      <div className="ls-rank-cell">
                        {rankBadge.icon}
                        <span className="ls-rank-number">{standing.rank}</span>
                      </div>
                    </td>
                    <td className="ls-participant-col">
                      <span className="ls-participant-name">{standing.participantName}</span>
                    </td>
                    <td className="ls-stat-col">{standing.matchesPlayed}</td>
                    <td className="ls-stat-col ls-wins">{standing.wins}</td>
                    <td className="ls-stat-col ls-losses">{standing.losses}</td>
                    <td className="ls-stat-col">{standing.winPercentage.toFixed(1)}%</td>
                    <td className="ls-stat-col">{standing.pointsFor}</td>
                    <td className="ls-stat-col">{standing.pointsAgainst}</td>
                    <td className={`ls-stat-col ls-diff ${pointDiff > 0 ? 'ls-positive' : pointDiff < 0 ? 'ls-negative' : ''}`}>
                      {pointDiff > 0 ? '+' : ''}{pointDiff}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showPlayoffsModal && (
        <GeneratePlayoffsModal
          leagueId={leagueId}
          standings={standings}
          matches={matches}
          onClose={() => setShowPlayoffsModal(false)}
          onSuccess={handlePlayoffsGenerated}
        />
      )}
    </section>
  );
};

export default StandingsTab;
